import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  Heading,
  Input,
  InputGroup,
  InputLeftAddon,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { UserContext } from 'context/provider/UserProvider';
import { getUserProfile, putUserProfile } from 'services/user-profile';
import { useFormik } from 'formik';
import { RegisterUserFormValues, UserProfile } from 'types/user';
import profileRegisterSchema from 'schemas/profile-register';
import FieldErrorMessage from 'components/Form/FieldErrorMessage/FieldErrorMessage';
import { InfoOutlineIcon } from '@chakra-ui/icons';
import { FaArrowLeft } from 'react-icons/all';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  AutoComplete,
  AutoCompleteInput,
  AutoCompleteItem,
  AutoCompleteList,
} from '@choc-ui/chakra-autocomplete';
import countries from 'views/ProfileRegister/countries';
import Dialog from 'components/shared/Dialog';

export default function ProfileEdit() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const toast = useToast();

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [userInfo, setUserInfo] = useState<UserProfile>();

  const {
    state: {
      user: { uid },
    },
  } = useContext(UserContext);

  const goToPreviousPage = () => {
    navigate(-1);
  };

  const {
    values,
    errors,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    isValid,
    dirty,
    setValues,
  } = useFormik<RegisterUserFormValues>({
    initialValues: {
      firstName: userInfo?.firstName as string,
      lastName: userInfo?.lastName as string,
      payoneerEmail: userInfo?.payoneerEmail,
      phoneNumber: userInfo?.phoneNumber,
      country: userInfo?.country,
      linkedinUrl: userInfo?.linkedinUrl,
    },
    enableReinitialize: true,
    validateOnMount: true,
    validationSchema: profileRegisterSchema,
    onSubmit: async ({ phoneNumber, linkedinUrl, country, payoneerEmail }) => {
      const response = await putUserProfile({
        uid,
        payload: { phoneNumber, linkedinUrl, country, payoneerEmail },
      });
      if (response?.status === 200) {
        toast({
          title: t('profile_edit.success_message.title'),
          description: t('profile_edit.success_message.description'),
          status: 'success',
          duration: 9000,
          isClosable: true,
        });
      } else {
        toast({
          title: t('profile_edit.error_message.title'),
          description: t('profile_edit.error_message.description'),
          status: 'error',
          duration: 9000,
          isClosable: true,
        });
      }
      setFormSubmitted((state) => !state);
    },
  });

  useEffect(() => {
    getUserProfile(uid).then((res) => setUserInfo(res?.data));
  }, [formSubmitted]);

  return (
    <Box pb={16}>
      <Button
        leftIcon={<FaArrowLeft />}
        variant="ghost"
        onClick={dirty ? onOpen : goToPreviousPage}
        p={0}
        color="gray.700"
        mb={6}
      >
        {t('global.back')}
      </Button>
      <Heading as="h2" variant="title5">
        {t('global.profile')}
      </Heading>
      <form onSubmit={handleSubmit}>
        <Flex flexDir="column">
          <Heading
            as="h3"
            variant="title6"
            mb={4}
            mt={8}
            textAlign="start"
            fontWeight={500}
            fontSize="1.125rem"
          >
            {t('profile_registration.personal_information')}
          </Heading>
          <Grid
            templateColumns="repeat(auto-fit, minmax(217px, 1fr))"
            gap="24px"
            maxW="696px"
          >
            <FormControl>
              <FormLabel>
                {t('profile_registration.form_fields.first_name')}
              </FormLabel>
              <Input
                readOnly
                isDisabled
                type="text"
                name="firstName"
                value={values.firstName}
                variant="elevated"
              />
            </FormControl>
            <FormControl>
              <FormLabel>
                {t('profile_registration.form_fields.last_name')}
              </FormLabel>
              <Input
                readOnly
                isDisabled
                type="text"
                name="lastName"
                value={values.lastName}
                variant="elevated"
              />
            </FormControl>
            <FormControl>
              <FormLabel>
                {t('profile_registration.form_fields.phone')}
              </FormLabel>
              <Input
                type="tel"
                name="phoneNumber"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.phoneNumber}
                variant="elevated"
                placeholder={`${t(
                  'profile_registration.form_fields.placeholders.phone'
                )}`}
                {...(errors.phoneNumber ? { borderColor: 'crimson' } : {})}
              />
              {errors.phoneNumber && (
                <FieldErrorMessage
                  color="brand.700"
                  icon={<InfoOutlineIcon />}
                  fontSize="xs"
                  variant="caption1"
                  text={t(errors.phoneNumber)}
                />
              )}
            </FormControl>
            <FormControl>
              <FormLabel>
                {t('profile_registration.form_fields.linkedin')}
              </FormLabel>
              <InputGroup variant="elevated">
                <InputLeftAddon children="linkedin.com/in/" />
                <Input
                  type="text"
                  name="linkedinUrl"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.linkedinUrl}
                  placeholder={`${t(
                    'profile_registration.form_fields.placeholders.linkedin'
                  )}`}
                  {...(errors.linkedinUrl ? { borderColor: 'crimson' } : {})}
                />
              </InputGroup>
              {errors.linkedinUrl && (
                <FieldErrorMessage
                  color="brand.700"
                  icon={<InfoOutlineIcon />}
                  fontSize="xs"
                  variant="caption1"
                  text={t(errors.linkedinUrl)}
                />
              )}
            </FormControl>
            <FormControl>
              <FormLabel>
                {t('profile_registration.form_fields.country_of_residence')}
              </FormLabel>
              <AutoComplete
                openOnFocus
                onSelectOption={({ item: { value } }) => {
                  setValues({ ...values, country: value });
                }}
                restoreOnBlurIfEmpty={false}
              >
                <AutoCompleteInput
                  autoComplete="none"
                  name="country"
                  onBlur={(e) =>
                    setValues({ ...values, country: e.target.value })
                  }
                  onChange={(e) =>
                    setValues({ ...values, country: e.target.value })
                  }
                  placeholder={`${t(
                    'profile_registration.form_fields.placeholders.country_of_residence'
                  )}`}
                  value={values.country}
                  variant="elevated"
                  {...(errors.country ? { borderColor: 'crimson' } : {})}
                />
                <AutoCompleteList>
                  {countries.map((country, cid) => (
                    <AutoCompleteItem
                      key={`option-${cid}`}
                      value={country}
                      textTransform="capitalize"
                    >
                      {country}
                    </AutoCompleteItem>
                  ))}
                </AutoCompleteList>
              </AutoComplete>{' '}
            </FormControl>
            <Flex justifyContent="flex-end" gap={6}>
              <Button
                type="submit"
                isDisabled={isSubmitting || !dirty || !isValid}
                my={29}
                w="max-content"
              >
                {t('global.save')}
              </Button>
            </Flex>
          </Grid>
          <Flex flexDir="column">
            <Heading
              as="h3"
              variant="title6"
              mb={4}
              mt={8}
              textAlign="start"
              fontWeight={500}
              fontSize="1.125rem"
            >
              {t('profile_registration.billing_information')}
            </Heading>
            <Grid
              autoFlow="columns"
              templateColumns="repeat(auto-fill, minmax(217px, 1fr))"
              columnGap="24px"
              maxW="696px"
            >
              <FormControl>
                <FormLabel>
                  {t('profile_registration.form_fields.payoneer_email')}
                </FormLabel>
                <Input
                  type="text"
                  name="payoneerEmail"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.payoneerEmail}
                  variant="elevated"
                  placeholder={`${t(
                    'profile_registration.form_fields.placeholders.payoneer_email'
                  )}`}
                  {...(errors.payoneerEmail ? { borderColor: 'crimson' } : {})}
                />
                {errors.payoneerEmail && (
                  <FieldErrorMessage
                    color="brand.700"
                    icon={<InfoOutlineIcon />}
                    fontSize="xs"
                    variant="caption1"
                    text={t(errors.payoneerEmail)}
                  />
                )}
              </FormControl>
            </Grid>
          </Flex>
        </Flex>
      </form>
      <Dialog
        onClose={onClose}
        onConfirm={goToPreviousPage}
        withCancelButton
        isOpen={isOpen}
        title={`${t('profile_edit.changes_lost_dialog.title')}`}
        description={`${t('profile_edit.changes_lost_dialog.description')}`}
        buttonText={`${t('global.accept')}`}
      />
    </Box>
  );
}
